import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Longest prefix wins, so '/admin/companies' is matched before '/admin'.
const pageTitles = {
  '/admin/companies': 'Companies',
  '/admin/company': 'Company Profile',
  '/admin/dashboard': 'Dashboard',
  '/admin/templates': 'Templates',
  '/admin/links': 'Link Management',
  '/admin/submissions': 'Submissions',
  '/admin/users': 'Users',
  '/admin/clinics': 'Companies',
  '/admin/trash': 'Trash',
  '/admin/security': 'Security Settings',
  '/admin/change-password': 'Change Password',
  '/portal/templates': 'My Templates',
  '/portal/links': 'My Links',
  '/portal/submissions': 'My Submissions',
};

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 60,
    padding: '0 24px',
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
    zIndex: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: 650,
    color: '#111827',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    position: 'relative',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: 8,
    transition: 'background 0.15s',
  },
  avatar: {
    width: 35,
    height: 35,
    borderRadius: '50%',
    background: 'var(--company-primary-color, #0e7c8c)',
    color: 'var(--company-primary-contrast, #fff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
    textTransform: 'uppercase',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.3,
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
  },
  userRole: {
    fontSize: 12,
    color: '#6b7280',
  },
  roleBadge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  adminBadge: {
    background: '#e0f2f7',
    color: 'var(--company-primary-color, #273947)',
  },
  clinicBadge: {
    background: '#e0f2f7',
    color: 'var(--company-primary-color, #0e7c8c)',
  },
  chevron: {
    fontSize: 10,
    color: '#9ca3af',
    marginLeft: 4,
    transition: 'transform 0.2s',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 6,
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
    minWidth: 200,
    overflow: 'hidden',
    zIndex: 100,
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    fontSize: 14,
    color: '#374151',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    transition: 'background 0.15s',
  },
  dropdownDivider: {
    height: 1,
    background: '#e5e7eb',
    margin: '4px 0',
  },
  logoutItem: {
    color: '#dc2626',
  },
};

export default function PortalHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isSuperadmin = user?.role === 'superadmin';
  const isCompanyAdmin = user?.role === 'companyadmin';
  // Both admin roles get the account menu; only the platform owner sees
  // platform-wide settings.
  const isAdmin = isSuperadmin || isCompanyAdmin;
  const displayName = user?.displayName || user?.username || 'User';
  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Longest match first: '/admin/company' must not win over '/admin/companies'.
  const pathKey = Object.keys(pageTitles)
    .filter((key) => location.pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
  const pageTitle = pathKey
    ? pageTitles[pathKey]
    : location.pathname.includes('/editor/')
      ? 'Field Editor'
      : 'Dashboard';

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate('/', { replace: true });
  };

  const handleChangePassword = () => {
    setDropdownOpen(false);
    navigate(isAdmin ? '/admin/change-password' : '/portal/change-password');
  };

  // A company admin also records its own branding; the platform owner does not
  // belong to a company, so the link would have nothing to edit.
  const handleCompanyProfile = () => {
    setDropdownOpen(false);
    navigate('/admin/company');
  };

  const handleSecuritySettings = () => {
    setDropdownOpen(false);
    navigate('/admin/security');
  };

  const roleLabel = isSuperadmin ? 'Platform Owner' : isCompanyAdmin ? 'Company Admin' : 'Staff';
  const badgeStyle = {
    ...styles.roleBadge,
    ...(isAdmin ? styles.adminBadge : styles.clinicBadge),
  };

  return (
    <div style={styles.header}>
      <h1 style={styles.title}>{pageTitle}</h1>

      <div
        ref={dropdownRef}
        style={styles.userSection}
        onClick={() => setDropdownOpen((prev) => !prev)}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={styles.avatar}>{initials}</div>
        <div style={styles.userInfo}>
          <span style={styles.userName}>{displayName}</span>
          <span style={badgeStyle}>{roleLabel}</span>
        </div>
        <span style={{ ...styles.chevron, transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
          ▼
        </span>

        {dropdownOpen && (
          <div style={styles.dropdown}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{displayName}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {user?.clinicName || user?.username}
              </div>
            </div>

            {isAdmin && (
              <button
                style={styles.dropdownItem}
                onClick={(e) => { e.stopPropagation(); handleChangePassword(); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Change Password
              </button>
            )}

            {isCompanyAdmin && (
              <button
                style={styles.dropdownItem}
                onClick={(e) => { e.stopPropagation(); handleCompanyProfile(); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Company Profile
              </button>
            )}

            {isSuperadmin && (
              <button
                style={styles.dropdownItem}
                onClick={(e) => { e.stopPropagation(); handleSecuritySettings(); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Security Settings
              </button>
            )}

            <div style={styles.dropdownDivider} />

            <button
              style={{ ...styles.dropdownItem, ...styles.logoutItem }}
              onClick={(e) => { e.stopPropagation(); handleLogout(); }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
